import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { resumirConversacion } from '../gemini';
import {
  MessagesSquare,
  Loader2,
  Send,
  Sparkles,
  ArrowLeft,
  User,
  Bot,
  ShieldCheck,
} from 'lucide-react';

const fmtFecha = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return '';
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function Conversaciones({ selPhone: selPhoneProp, onSelPhone }) {
  const [convs, setConvs] = useState([]);
  const [selInterno, setSelInterno] = useState(null);
  // Selección controlada desde el panel si se pasan props; si no, interna.
  const selPhone = selPhoneProp !== undefined ? selPhoneProp : selInterno;
  const setSelPhone = onSelPhone || setSelInterno;
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resumen, setResumen] = useState('');
  const [resumiendo, setResumiendo] = useState(false);
  const scrollRef = useRef(null);

  // Lista de conversaciones, más recientes primero.
  useEffect(() => {
    const q = query(collection(db, 'chat_conversations'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => setConvs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('Error listando conversaciones:', e)
    );
    return () => unsub();
  }, []);

  // Mensajes de la conversación seleccionada.
  useEffect(() => {
    if (!selPhone) {
      setMensajes([]);
      return;
    }
    setResumen('');
    const q = query(
      collection(db, 'chat_conversations', selPhone, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => setMensajes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('Error leyendo mensajes:', e)
    );
    return () => unsub();
  }, [selPhone]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [mensajes]);

  const enviar = async (e) => {
    e?.preventDefault();
    const t = texto.trim();
    if (!t || !selPhone || enviando) return;
    setEnviando(true);
    try {
      // Referenciar el último mensaje del cliente, si existe.
      const ultimoCliente = [...mensajes].reverse().find((m) => m.role === 'user');
      await addDoc(collection(db, 'chat_conversations', selPhone, 'messages'), {
        role: 'assistant',
        text: t,
        tipo: 'admin',
        createdAt: serverTimestamp(),
        ...(ultimoCliente ? { enRespuestaA: ultimoCliente.text } : {}),
      });
      await setDoc(
        doc(db, 'chat_conversations', selPhone),
        { updatedAt: serverTimestamp() },
        { merge: true }
      );
      setTexto('');
    } catch (err) {
      console.error('Error enviando respuesta:', err);
      alert('No se pudo enviar la respuesta.');
    } finally {
      setEnviando(false);
    }
  };

  const generarResumen = async () => {
    if (!mensajes.length || resumiendo) return;
    setResumiendo(true);
    setResumen('');
    try {
      const r = await resumirConversacion(
        mensajes
          .filter((m) => m.tipo !== 'sin_respuesta')
          .map((m) => ({ role: m.role, text: m.text }))
      );
      setResumen(r);
    } catch (err) {
      console.error('Error generando resumen:', err);
      setResumen('No se pudo generar el resumen: ' + (err.message || 'error desconocido'));
    } finally {
      setResumiendo(false);
    }
  };

  return (
    <section className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <MessagesSquare size={18} className="text-blue-400" />
        <h2 className="font-bold">Conversaciones</h2>
        {convs.length > 0 && (
          <span className="ml-1 text-[11px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded-full px-2 py-0.5">
            {convs.length}
          </span>
        )}
      </div>
      <p className="text-slate-400 text-xs mb-4">
        Lee los chats de los clientes, responde directamente y genera un resumen con IA.
      </p>

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        {/* Lista de conversaciones */}
        <div className={`${selPhone ? 'hidden md:block' : 'block'}`}>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {convs.length === 0 && (
              <div className="text-slate-500 text-sm text-center py-6">
                Aún no hay conversaciones.
              </div>
            )}
            {convs.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelPhone(c.id)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${
                  selPhone === c.id
                    ? 'bg-blue-600/20 border-blue-500/50'
                    : 'bg-slate-900/50 border-slate-700/50 hover:bg-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <User size={14} className="text-slate-400 shrink-0" />
                  <span className="text-slate-100 text-sm font-mono truncate">{c.phone || c.id}</span>
                </div>
                {c.updatedAt && (
                  <div className="text-slate-500 text-[11px] mt-0.5">{fmtFecha(c.updatedAt)}</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Hilo de la conversación */}
        <div className={`${selPhone ? 'block' : 'hidden md:block'}`}>
          {!selPhone ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm py-10 border border-dashed border-slate-700/50 rounded-xl">
              Selecciona una conversación para verla.
            </div>
          ) : (
            <div className="flex flex-col border border-slate-700/50 rounded-xl overflow-hidden">
              {/* Encabezado del hilo */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-900/60 border-b border-slate-700/50">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    onClick={() => setSelPhone(null)}
                    className="md:hidden p-1 rounded-lg hover:bg-slate-800 text-slate-400"
                    title="Volver"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <span className="text-slate-200 text-sm font-mono truncate">{selPhone}</span>
                </div>
                <button
                  onClick={generarResumen}
                  disabled={resumiendo || !mensajes.length}
                  className="shrink-0 bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 text-white text-xs font-bold py-1.5 px-3 rounded-lg flex items-center gap-1.5"
                >
                  {resumiendo ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                  Resumen
                </button>
              </div>

              {/* Resumen generado */}
              {resumen && (
                <div className="px-3 py-2.5 bg-purple-500/10 border-b border-purple-500/30 text-purple-100 text-xs whitespace-pre-wrap leading-relaxed max-h-52 overflow-y-auto">
                  <div className="flex items-center gap-1.5 text-purple-300 font-bold mb-1">
                    <Sparkles size={12} /> Resumen
                  </div>
                  {resumen}
                </div>
              )}

              {/* Mensajes */}
              <div ref={scrollRef} className="p-3 space-y-2.5 max-h-[45vh] overflow-y-auto bg-slate-950/40">
                {mensajes.length === 0 && (
                  <div className="text-slate-500 text-sm text-center py-6">Sin mensajes.</div>
                )}
                {mensajes.map((m) => {
                  const esUsuario = m.role === 'user';
                  const esAdmin = m.tipo === 'admin';
                  return (
                    <div key={m.id} className={`flex gap-2 ${esUsuario ? 'justify-start' : 'justify-end'}`}>
                      {esUsuario && (
                        <div className="w-7 h-7 shrink-0 rounded-full bg-slate-700 flex items-center justify-center">
                          <User size={14} className="text-slate-300" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                          esUsuario
                            ? 'bg-slate-800 text-slate-100 border border-slate-700 rounded-bl-md'
                            : esAdmin
                              ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-100 rounded-br-md'
                              : 'bg-blue-600/90 text-white rounded-br-md'
                        }`}
                      >
                        {esAdmin && (
                          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 mb-0.5">
                            Tú (equipo)
                          </div>
                        )}
                        {m.tipo === 'imagen' ? (
                          <div>
                            {m.esComprobante && (
                              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 mb-1">
                                Comprobante de pago
                              </div>
                            )}
                            <a href={m.imageUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={m.imageUrl}
                                alt={m.text || 'imagen'}
                                loading="lazy"
                                className="rounded-lg max-h-48 w-auto object-cover border border-white/10"
                              />
                            </a>
                          </div>
                        ) : (
                          m.text
                        )}
                      </div>
                      {!esUsuario && (
                        <div className="w-7 h-7 shrink-0 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                          {esAdmin ? (
                            <ShieldCheck size={14} className="text-emerald-400" />
                          ) : (
                            <Bot size={14} className="text-blue-400" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Responder */}
              <form onSubmit={enviar} className="flex items-end gap-2 p-2.5 border-t border-slate-700/50 bg-slate-900/60">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      enviar();
                    }
                  }}
                  rows={1}
                  placeholder="Responde al cliente…"
                  className="flex-1 resize-none bg-slate-800 border border-slate-700 rounded-xl text-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 max-h-28"
                />
                <button
                  type="submit"
                  disabled={enviando || !texto.trim()}
                  className="w-10 h-10 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white flex items-center justify-center"
                >
                  {enviando ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
